import {
  faceNotation,
  sameSquare,
  type MatchView,
  type ReplayCheck,
  type Side,
  veiledFrom,
} from '../../engine';
import { t } from '../../i18n';
import { itemName, itemText, omenName, pawnRef, statusName, type Names } from '../../app/describe';
import { RouteGlyph } from './RouteGlyph';
import { RouteSteps } from './RouteSteps';

/**
 * A pawn held down (a long press, or a right click): its Inscription, the route it would leave
 * as an Epitaph if captured, over the board, without touching the move being made. While an
 * Epitaph is being replayed, also what this pawn would make of it (its route is drawn on the
 * board meanwhile). It sits in the half of the board away from the pawn.
 */
export function PawnPeek({
  v,
  pawnId,
  viewer,
  names,
  flipRoute,
  replay,
  place,
  onClose,
}: {
  v: MatchView;
  pawnId: string;
  viewer: Side;
  names: Names;
  flipRoute: boolean;
  /** The Epitaph being replayed, as this pawn would replay it. */
  replay: ReplayCheck | null;
  /** Where on the board: away from the pawn. */
  place: 'top' | 'bottom';
  onClose: () => void;
}) {
  const pawn = v.pawns[pawnId];
  if (!pawn) return null;
  const inscription = pawn.inscription;
  const name = names.pawn(pawnId);
  return (
    <section
      className={`pawn-peek pawn-peek-${place}`}
      role="dialog"
      aria-label={t('peek.label', { pawn: name })}
    >
      <header className="pawn-peek-head">
        <h3>
          {name} <span className="dim">({t(`side.${pawn.side}`)})</span>
        </h3>
        <button
          type="button"
          className="notice-close"
          aria-label={t('action.close')}
          onClick={onClose}
        >
          <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
            <path d="M3.5 3.5l9 9M12.5 3.5l-9 9" stroke="currentColor" strokeWidth="2" />
          </svg>
        </button>
      </header>
      {v.ledger && <PawnLedger v={v} pawnId={pawnId} />}
      {replay && (
        <p className="pawn-peek-replay">
          {replay.ok ? (
            <>
              <code>
                {replay.steps.map((x) => faceNotation(x.link.face, x.link.wasWild)).join(' → ')}
              </code>{' '}
              <strong>
                {t('preview.formula', {
                  p: replay.breakdown.points.total,
                  m: replay.breakdown.multiplier.total,
                  score: replay.breakdown.score,
                })}
              </strong>
              {replay.captures.length > 0 && (
                <span className="good">
                  {' · '}
                  {t('preview.captures', { n: replay.captures.length })}
                </span>
              )}
            </>
          ) : (
            t('epitaph.cannot', {
              pawn: pawnRef(v, pawnId, names),
              link: replay.failedAt + 1,
              reason: t(`epitaph.fail.${replay.reason}`),
            })
          )}
        </p>
      )}
      {inscription ? (
        <div className="route-block">
          <span className="mini-label">{t('inscription.label')}</span>
          <RouteGlyph links={inscription.links} size={52} flip={flipRoute} />
          <RouteSteps links={inscription.links} owner={pawn.side} />
          <span className="dim">{t('epitaph.score', { score: inscription.recordedScore })}</span>
          <p className="pawn-peek-note">
            {pawn.side === viewer
              ? t('peek.mine')
              : t('peek.theirs', { name: names.side(pawn.side) })}
          </p>
        </div>
      ) : veiledFrom(v.config, viewer) === pawn.side ? (
        <p className="pawn-peek-note dim">{t('peek.veiled')}</p>
      ) : (
        <p className="pawn-peek-note dim">{t('peek.none')}</p>
      )}
    </section>
  );
}

/** Long Night: what a pawn's statuses, Carvings and the Omen under it do. */
function PawnLedger({ v, pawnId }: { v: MatchView; pawnId: string }) {
  const pawn = v.pawns[pawnId];
  const statuses = v.ledger?.statuses[pawnId] ?? [];
  const carvings = pawn?.carvings ?? [];
  const omen = pawn?.square
    ? v.ledger?.omens.find((o) => !o.spent && sameSquare(o.square, pawn.square))
    : undefined;
  if (statuses.length + carvings.length === 0 && !omen) return null;
  return (
    <ul className="pawn-peek-ledger">
      {statuses.map((st) => (
        <li key={st.kind}>
          <strong>{statusName(st.kind)}</strong> {itemText(`status.${st.kind}`)}
        </li>
      ))}
      {carvings.map((c) => (
        <li key={c.id}>
          <strong>{itemName(c.id)}</strong> {itemText(c.id)}
        </li>
      ))}
      {omen && (
        <li>
          <strong>{omenName(omen.kind)}</strong> {itemText(`omen.${omen.kind}`)}
        </li>
      )}
    </ul>
  );
}
